import { yupResolver } from "@hookform/resolvers/yup";
import {
  DeleteConfirmation,
  Divider, ErrorMessages, IconButton, IconButtonType, Modal, Page, Spinner
} from "@whitespace/whitespace-ui";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import IHwrcSite from "interfaces/IHwrcSite";
import IRegion from "interfaces/IRegion";
import IUpdateHwrcSiteRequest from "interfaces/Request/IUpdateHwrcSiteRequestModel";
import { useStateMachine } from "little-state-machine";
import { normaliseLatLong } from "modules/Normalise";
import hwrcSiteSchema from "modules/schemas/hwrcSiteSchema";
import { useCallback, useEffect, useState } from "react";
import {
  Breadcrumb, Button, Card, Col, Form, Row
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

const UpdateHwrcSite = () => {
  const { state } = useStateMachine();
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [updateButtonDisabled, setUpdateButtonDisabled] = useState<boolean>(true);
  const [deleteButtonPressed, setDeleteButtonPressed] = useState<boolean>(false);
  const [hwrcSite, setHwrcSite] = useState<IHwrcSite>({ ...state.selectedHwrcSite! });
  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    setValue
  } = useForm<IUpdateHwrcSiteRequest>({
    resolver: yupResolver(hwrcSiteSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const navigate = useNavigate();

  const {
    isSending: getRegionsIsSending,
    errors: getRegionsErrors,
    data: getRegionsData,
    sendRequest: getRegionsSendRequest
  } = useAmplifyREST();

  const {
    isSending: updateHwrcSiteIsSending,
    errors: updateHwrcSiteErrors,
    statusCode: updateHwrcSiteStatusCode,
    sendRequest: updateHwrcSiteSendRequest
  } = useAmplifyREST();

  const {
    isSending: deleteHwrcSiteIsSending,
    errors: deleteHwrcSiteErrors,
    statusCode: deleteHwrcSiteStatusCode,
    sendRequest: deleteHwrcSiteSendRequest
  } = useAmplifyREST();

  const isSending = getRegionsIsSending || updateHwrcSiteIsSending || deleteHwrcSiteIsSending;
  const apiErrors = getRegionsErrors || updateHwrcSiteErrors || deleteHwrcSiteErrors;

  useEffect(() => {
    getRegionsSendRequest("settings/regions", "GET");
  }, [getRegionsSendRequest]);

  useEffect(() => {
    if (apiErrors) setShowExceptions(true);
  }, [apiErrors]);

  const canFormBeSubmitted = () => {
    const name = getValues("name");
    const code = getValues("code");
    const regionId = getValues("regionId");
    const latitude = getValues("latitude") ?? "";
    const longitude = getValues("longitude") ?? "";
    const active = getValues("active");

    const requiredFieldsEntered = (
      name.length > 0
      && (hwrcSite.inUse || code.length > 0)
      && regionId.length > 0);

    const atLeastOneFieldChangedValue = (
      name !== hwrcSite.name
      || (!hwrcSite.inUse && code !== hwrcSite.code)
      || regionId !== hwrcSite.regionId
      || (hwrcSite.latitude ? latitude !== hwrcSite.latitude.toString() : !hwrcSite.latitude && latitude.length > 0)
      || (hwrcSite.longitude ? longitude !== hwrcSite.longitude.toString() : !hwrcSite.longitude && longitude.length > 0)
      || active !== hwrcSite.active
    );

    setUpdateButtonDisabled(!(requiredFieldsEntered && atLeastOneFieldChangedValue));
  };

  const handleUpdateHwrcSiteClick = (request: IUpdateHwrcSiteRequest) => {
    if (hwrcSite.inUse) {
      request.code = hwrcSite.code;
    }
    if (request.latitude?.length === 0) {
      request.latitude = undefined;
    }

    if (request.longitude?.length === 0) {
      request.longitude = undefined;
    }
    updateHwrcSiteSendRequest(`settings/hwrcsites/${hwrcSite.id}`, "PUT", request);
  };

  useEffect(() => {
    if (updateHwrcSiteStatusCode === 200) navigate(RoutePathsEnum.SettingsHwrcSites);
  }, [updateHwrcSiteStatusCode]);

  const handleDeleteHwrcSite = () => {
    deleteHwrcSiteSendRequest(`settings/hwrcsites/${hwrcSite.id}`, "DELETE");
  };

  useEffect(() => {
    if (deleteHwrcSiteStatusCode === 200) navigate(RoutePathsEnum.SettingsHwrcSites);
  }, [deleteHwrcSiteStatusCode]);

  const renderRegions = () => {
    const regions = [
      <option key="" value="">Please Select</option>
    ];
    if (getRegionsData) {
      (getRegionsData.regions as IRegion[]).forEach(region => {
        regions.push(<option key={region.id} value={region.id}>{region.name}</option>);
      });
    }

    return regions;
  };

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="hwrc-title">
              Edit HWRC Site
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Form autoComplete="off" onSubmit={handleSubmit(handleUpdateHwrcSiteClick)}>
          <Card.Body className="no-padding">
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-settings"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.Settings }}
                    onClick={() => navigate(RoutePathsEnum.Settings)}
                  >
                    Settings
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    data-testid="breadcrumb-hwrcsites"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.SettingsHwrcSites }}
                    onClick={() => navigate(RoutePathsEnum.SettingsHwrcSites)}

                  >
                    HWRC Sites
                  </Breadcrumb.Item>
                  <Breadcrumb.Item data-testid="breadcrumb-updatehwrcsite" active>
                    Edit
                    {" "}
                    {hwrcSite?.name}
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>
              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Delete}
                  variant="danger"
                  className="right-margin-small"
                  disabled={hwrcSite.inUse || isSending}
                  aria-disabled={hwrcSite.inUse || isSending}
                  aria-label="Delete HWRC Site"
                  type="button"
                  spin={deleteHwrcSiteIsSending}
                  onClick={() => setDeleteButtonPressed(true)}
                  data-testid="btnDelete"
                >
                  Delete
                </IconButton>
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={updateButtonDisabled || isSending}
                  aria-disabled={updateButtonDisabled || isSending}
                  aria-label="Update HWRC Site"
                  type="submit"
                  spin={updateHwrcSiteIsSending}
                  data-testid="btnUpdate"
                >
                  Update
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(RoutePathsEnum.SettingsHwrcSites)}
                  data-testid="cancel"
                  aria-label="Cancel update and back to HWRC Sites"
                >
                  Cancel
                </Button>
              </Col>
            </Row>
            <Divider />
            <section className="hwrcsites pt-2">
              <Row className="d-flex mt-1 ms-1">
                <Col xs={12}>
                  <div className="control-pane">
                    <div className="control-section" />
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="name">
                        <Form.Label id="siteNameLabel" className="horiz-form-label">
                          Site Name:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            {...register("name")}
                            defaultValue={hwrcSite?.name}
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-invalid={ariaInvalidCheck("name", errors, null)}
                            aria-labelledby="siteNameLabel"
                            maxLength={50}
                            onKeyUp={() => canFormBeSubmitted()}
                          />
                        </Col>
                        <ErrorMessages fieldName="name" errors={errors} validationFailures={null} />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="code">
                        <Form.Label id="siteCodeLabel" className="horiz-form-label">
                          Site Code (must align with mobile system):
                        </Form.Label>
                        <Col>
                          <Form.Control
                            {...register("code")}
                            defaultValue={hwrcSite?.code}
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-invalid={ariaInvalidCheck("code", errors, null)}
                            aria-labelledby="siteCodeLabel"
                            maxLength={50}
                            onKeyUp={() => canFormBeSubmitted()}
                            disabled={hwrcSite.inUse}
                          />
                        </Col>
                        <ErrorMessages fieldName="code" errors={errors} validationFailures={null} />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="regionId">
                        <Form.Label id="regionLabel" className="horiz-form-label">
                          Region:
                        </Form.Label>
                        <Col>
                          {getRegionsData && (
                            <Form.Select
                              {...register("regionId", { onChange: () => canFormBeSubmitted() })}
                              defaultValue={hwrcSite.regionId}
                              data-testid="ddlRegion"
                              aria-required
                              aria-invalid={false}
                              aria-labelledby="regionLabel"
                            >
                              {renderRegions()}
                            </Form.Select>
                          )}
                        </Col>
                        <ErrorMessages fieldName="regionId" errors={errors} validationFailures={null} />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="latitude">
                        <Form.Label id="latitudeLabel" className="horiz-form-label">
                          Latitude:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            {...register("latitude")}
                            defaultValue={hwrcSite?.latitude?.toString()}
                            type="text"
                            role="textbox"
                            aria-required="false"
                            aria-invalid={ariaInvalidCheck("latitude", errors, null)}
                            aria-labelledby="latitudeLabel"
                            maxLength={50}
                            onChange={e => { e.target.value = normaliseLatLong(e.target.value); setValue("latitude", e.target.value); }}
                            onKeyUp={() => canFormBeSubmitted()}

                          />
                        </Col>
                        <ErrorMessages fieldName="latitude" errors={errors} validationFailures={null} />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="longitude">
                        <Form.Label id="longitudeLabel" className="horiz-form-label">
                          Longitude:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            {...register("longitude")}
                            defaultValue={hwrcSite?.longitude?.toString()}
                            type="text"
                            role="textbox"
                            aria-required="false"
                            aria-invalid={ariaInvalidCheck("longitude", errors, null)}
                            aria-labelledby="longitudeLabel"
                            maxLength={50}
                            onChange={e => { e.target.value = normaliseLatLong(e.target.value); setValue("longitude", e.target.value); }}
                            onKeyUp={() => canFormBeSubmitted()}
                          />
                        </Col>
                        <ErrorMessages fieldName="longitude" errors={errors} validationFailures={null} />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2 mt-3">
                      <Form.Group as={Row} controlId="active" className="d-flex align-items-center">
                        <Form.Label id="activeLabel" className="d-inline-flex horiz-form-label w-auto mb-0 me-3">
                          Active:
                        </Form.Label>
                        <Form.Check
                          {...register("active")}
                          type="switch"
                          className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                          aria-required="true"
                          aria-labelledby="activeLabel"
                          defaultChecked={hwrcSite?.active}
                          onChange={() => { setValue("active", !getValues("active")); canFormBeSubmitted(); }}
                        />
                        <ErrorMessages fieldName="active" errors={errors} validationFailures={null} />
                      </Form.Group>
                    </Row>
                  </div>
                </Col>
              </Row>
            </section>
          </Card.Body>
        </Form>
      </Card>
      <DeleteConfirmation
        title="Delete Site"
        onDelete={() => { handleDeleteHwrcSite(); }}
        onHide={() => { setDeleteButtonPressed(false); }}
        isDeleting={deleteHwrcSiteIsSending}
        show={deleteButtonPressed}

      >
        <p>Are you sure you want to delete this HWRC Site?</p>
      </DeleteConfirmation>

    </Page>
  );
};

export default UpdateHwrcSite;
